import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useCompleteProfileMutation,
  useGetProfileMetaQuery,
  useGetProfileQuery,
  useMeQuery,
} from "../api/authApi";
import {
  User,
  MapPin,
  Phone,
  Calendar,
  FileText,
  Camera,
  PenTool,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
} from "lucide-react";

const STORAGE_KEY = "logbook_auth_user_v2";

// Branding Helper Components (Matching your ApplicationDetails style)
function Row({ k, v, icon: Icon }: { k: string; v: any; icon?: any }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0 text-sm">
      <div className="text-gray-400 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="font-medium">{k}</span>
      </div>
      <div className="text-gray-900 font-semibold">{v || "—"}</div>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-[24px] shadow-sm overflow-hidden mb-6">
      <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">
          {title}
        </h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { refetch } = useMeQuery();
  const { data: meta, isLoading: metaLoading } = useGetProfileMetaQuery();
  const { data: profile } = useGetProfileQuery();
  const [completeProfile, { isLoading }] = useCompleteProfileMutation();

  const [form, setForm] = useState<any>({
    title: "",
    surname: "",
    firstname: "",
    othernames: "",
    sex: "",
    dob: "",
    phone: "",
    phone2: "",
    address: "",
    chapter_id: "",
    state_id: "",
    local_government_id: "",
    pro_pic: null,
    signature: null,
  });

  const [previews, setPreviews] = useState({ pro_pic: "", signature: "" });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (profile?.user?.member) {
      const member = profile.user.member;
      setForm((prev: any) => ({ ...prev, ...member }));
    }
  }, [profile]);

  // Nigerian Phone Validation: 070, 080, 081, 090, 091 (11 digits)
  const validateNGPhone = (num: string) => {
    const regex = /^(070|080|081|090|091|020)\d{8}$/;
    return regex.test(num);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "state_id") {
      setForm({ ...form, state_id: value, local_government_id: "" });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleFile = (e: any) => {
    const { name, files } = e.target;
    if (!files?.length) return;
    const file = files[0];
    setForm({ ...form, [name]: file });
    setPreviews({ ...previews, [name]: URL.createObjectURL(file) });
  };

  const filteredLGAs = useMemo(() => {
    if (!meta?.local_governments || !form.state_id) return [];
    return meta.local_governments.filter(
      (lga: any) => String(lga.state_id) === String(form.state_id)
    );
  }, [meta, form.state_id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateNGPhone(form.phone)) {
      setError(
        "Please enter a valid 11-digit Nigerian phone number (e.g., 08012345678)"
      );
      return;
    }

    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (form[key]) formData.append(key, form[key]);
      });

      await completeProfile(formData).unwrap();

      await refetch();

      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err: any) {
      setError(
        err?.data?.message || "Profile update failed. Please check your inputs."
      );
    }
  };

  if (metaLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-gray-400 gap-4 bg-gray-50">
        <Clock className="w-10 h-10 animate-pulse" />
        <p className="font-bold text-xs uppercase tracking-widest">
          Compiling Profile...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8 pb-20">
      <div className="space-y-2">
        <nav className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-tighter">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <span className="opacity-30">/</span>
          <span className="text-gray-900">Account Setup</span>
        </nav>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
          Complete Profile
        </h1>
        <p className="text-sm text-gray-500 font-medium">
          Please finalize your details to access the dashboard.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="grid lg:grid-cols-12 gap-8 items-start"
      >
        <div className="lg:col-span-8 space-y-6">
          <Section title="Identity" icon={User}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-style">Title</label>
                <input
                  name="title"
                  value={form.title}
                  placeholder="Title"
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
              <div>
                <label className="label-style">Surname</label>
                <input
                  name="surname"
                  value={form.surname}
                  required
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
              <div>
                <label className="label-style">First Name</label>
                <input
                  name="firstname"
                  value={form.firstname}
                  required
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
              <div>
                <label className="label-style">Other Names</label>
                <input
                  name="othernames"
                  value={form.othernames}
                  required
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
              <div>
                <label className="label-style">Gender</label>
                <select
                  name="sex"
                  value={form.sex}
                  required
                  onChange={handleChange}
                  className="input-style"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="label-style">Date of Birth</label>
                <input
                  name="dob"
                  type="date"
                  value={form.dob}
                  required
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
            </div>
          </Section>

          <Section title="Location & Membership" icon={MapPin}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-style">Chapter</label>
                <select
                  name="chapter_id"
                  value={form.chapter_id}
                  required
                  onChange={handleChange}
                  className="input-style"
                >
                  <option value="">Select Chapter</option>
                  {meta?.chapters?.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-style">State</label>
                <select
                  name="state_id"
                  value={form.state_id}
                  required
                  onChange={handleChange}
                  className="input-style"
                >
                  <option value="">Select State</option>
                  {meta?.states?.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-style">Local Government</label>
                <select
                  name="local_government_id"
                  value={form.local_government_id}
                  required
                  onChange={handleChange}
                  className="input-style"
                >
                  <option value="">Select LGA</option>
                  {filteredLGAs.map((lga: any) => (
                    <option key={lga.id} value={lga.id}>
                      {lga.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-style">Address</label>
                <textarea
                  name="address"
                  value={form.address}
                  required
                  onChange={handleChange}
                  rows={2}
                  className="input-style"
                />
              </div>
            </div>
          </Section>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <Section title="Contact" icon={Phone}>
            <div className="space-y-4">
              <div>
                <label className="label-style">Primary Phone (NG)</label>
                <input
                  name="phone"
                  value={form.phone}
                  placeholder="080..."
                  required
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
              <div>
                <label className="label-style">Alternative Phone</label>
                <input
                  name="phone2"
                  value={form.phone2}
                  placeholder="Optional"
                  onChange={handleChange}
                  className="input-style"
                />
              </div>
            </div>
          </Section>

          <Section title="Uploads" icon={FileText}>
            <div className="space-y-6">
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                <div className="w-20 h-20 bg-white rounded-full border shadow-sm overflow-hidden mb-3">
                  {previews.pro_pic ? (
                    <img
                      src={previews.pro_pic}
                      className="w-full h-full object-cover"
                      alt="Preview"
                    />
                  ) : (
                    <Camera className="w-full h-full p-6 text-gray-200" />
                  )}
                </div>
                <input
                  type="file"
                  name="pro_pic"
                  accept="image/*"
                  onChange={handleFile}
                  className="text-[10px] text-gray-500 font-bold"
                />
                <p className="text-[9px] text-gray-400 mt-1 uppercase font-black">
                  Profile Picture
                </p>
              </div>

              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                <div className="h-12 w-full bg-white rounded border shadow-sm flex items-center justify-center mb-3">
                  {previews.signature ? (
                    <img
                      src={previews.signature}
                      className="h-full object-contain p-1"
                      alt="Sig"
                    />
                  ) : (
                    <PenTool className="text-gray-200 w-6 h-6" />
                  )}
                </div>
                <input
                  type="file"
                  name="signature"
                  accept="image/*"
                  onChange={handleFile}
                  className="text-[10px] text-gray-500 font-bold"
                />
                <p className="text-[9px] text-gray-400 mt-1 uppercase font-black">
                  Electronic Signature
                </p>
              </div>
            </div>
          </Section>

          {/* Alert Logic */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 text-red-700 animate-shake">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-xs font-bold leading-tight">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border border-green-100 rounded-2xl flex items-center gap-3 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <p className="text-xs font-bold uppercase tracking-widest">
                Update Successful!
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || success}
            className="w-full py-4 bg-gray-900 text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] hover:bg-gray-800 transition-all shadow-lg disabled:opacity-50"
          >
            {isLoading ? "Processing..." : "Finish Setup"}
          </button>
        </div>
      </form>

      <style>{`
        .input-style {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
          outline: none;
          transition: all 0.2s;
        }
        .input-style:focus {
          border-color: #111827;
          box-shadow: 0 0 0 1px #111827;
        }
        .label-style {
          display: block;
          font-size: 10px;
          font-weight: 800;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.25rem;
          margin-left: 0.25rem;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
      `}</style>
    </div>
  );
};

export default CompleteProfilePage;
